'use client';

import { useState } from 'react';

import { useAddCartLines } from '@/entities/cart/client';
import {
  colorTint,
  defaultColorId,
  defaultSize,
  findVariant,
  isColorAvailable,
  primaryMedia,
  ProductMedia,
  purchaseBlocker,
  selectColor,
  selectSize,
  VariantPicker,
  type Product,
  type Selection,
} from '@/entities/product';
import { formatMoney, rub } from '@/shared/lib/money';
import { formatCount, THINGS_FORMS } from '@/shared/lib/plural';
import { Button } from '@/shared/ui/button';
import { Dialog } from '@/shared/ui/dialog';
import { Icon } from '@/shared/ui/icon';
import { TextButton, TextLink } from '@/shared/ui/text-link';

import styles from './buy-the-look.module.scss';

export type BuyTheLookProps = {
  product: Product;
  looks: Product[];
  selection: Selection;
  onColorChange: (colorId: string) => void;
  onSizeChange: (size: string) => void;
};

type Item = { product: Product; selection: Selection; current: boolean };

/** Price of the chosen variant, or the product's lowest price until a size is chosen. */
function priceOf(item: Item) {
  return findVariant(item.product, item.selection)?.price ?? item.product.price;
}

function initialSelection(product: Product): Selection {
  return { colorId: defaultColorId(product), size: defaultSize(product) };
}

/** "Купить весь образ": tiles in the product aside and a drawer that adds every item as its own line. */
export function BuyTheLook({
  product,
  looks,
  selection,
  onColorChange,
  onSizeChange,
}: BuyTheLookProps) {
  const [chosen, setChosen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries([product, ...looks].map((item) => [item.slug, true])),
  );
  const [picks, setPicks] = useState<Record<string, Selection>>(() =>
    Object.fromEntries(looks.map((item) => [item.slug, initialSelection(item)])),
  );
  const [tips, setTips] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);
  const [addedCount, setAddedCount] = useState<number | null>(null);
  const mutation = useAddCartLines();

  const all: Item[] = [
    { product, selection, current: true },
    ...looks.map((item) => ({
      product: item,
      selection: picks[item.slug] ?? initialSelection(item),
      current: false,
    })),
  ];
  const selected = all.filter((item) => chosen[item.product.slug]);
  const total = selected.reduce((sum, item) => sum + (priceOf(item)?.amountMinor ?? 0), 0);
  const needSize = selected.some((item) => purchaseBlocker(item.product, item.selection) !== null);
  const countLabel = formatCount(selected.length, THINGS_FORMS);
  const touch = () => setAddedCount(null);

  const toggle = (slug: string) => {
    touch();
    setChosen((current) => ({ ...current, [slug]: !current[slug] }));
  };
  const pickColor = (item: Item, colorId: string) => {
    touch();
    if (item.current) {
      onColorChange(colorId);
      return;
    }
    const result = selectColor(item.product, item.selection, colorId);
    setPicks((current) => ({ ...current, [item.product.slug]: result.selection }));
    setTips((current) => ({ ...current, [item.product.slug]: result.tip }));
  };
  const pickSize = (item: Item, size: string) => {
    touch();
    if (item.current) {
      onSizeChange(size);
      return;
    }
    const result = selectSize(item.product, item.selection, size);
    setPicks((current) => ({ ...current, [item.product.slug]: result.selection }));
    setTips((current) => ({ ...current, [item.product.slug]: result.tip }));
  };

  const addAll = () => {
    if (!selected.length || needSize) return;
    const lines = selected.flatMap((item) => {
      const variant = findVariant(item.product, item.selection);
      return variant ? [{ sku: variant.sku, quantity: 1, position: null }] : [];
    });
    mutation.mutate({ lines }, { onSuccess: () => setAddedCount(lines.length) });
  };

  return (
    <>
      <section className={styles.box} aria-labelledby="look-box-title">
        <div className={styles.head}>
          <h2 id="look-box-title" className={styles.title}>
            Купить весь образ
          </h2>
          <span className={styles.caption}>{countLabel}</span>
        </div>
        <div className={styles.tiles}>
          {all.map((item) => {
            const on = Boolean(chosen[item.product.slug]);
            const price = priceOf(item);
            return (
              <button
                key={item.product.slug}
                type="button"
                className={styles.tile}
                aria-pressed={on}
                onClick={() => toggle(item.product.slug)}
                aria-label={`${item.product.name}, ${on ? 'в образе' : 'не выбран'}`}
              >
                <ProductMedia
                  media={primaryMedia(item.product)}
                  fallbackLabel={item.product.name}
                  sizes="120px"
                  className={styles.thumb}
                  tint={colorTint(item.product, item.selection.colorId)}
                  placeholderSub=""
                />
                <span className={styles['tile-name']}>{item.product.name}</span>
                <span className={styles['tile-price']}>{price ? formatMoney(price) : '—'}</span>
                {on && (
                  <span className={styles.check} aria-hidden="true">
                    <Icon name="check-bold" size={12} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className={styles.total}>
          <span>Итого за {countLabel}</span>
          <span className={styles.sum}>{formatMoney(rub(total))}</span>
        </div>
        <Button
          variant="ghost"
          size="md"
          fullWidth
          className={styles.choose}
          data-empty={selected.length === 0 || undefined}
          onClick={() => {
            if (selected.length) setOpen(true);
          }}
        >
          Выбрать размеры
        </Button>
      </section>

      <Dialog
        theme="editorial"
        open={open}
        onOpenChange={setOpen}
        title="Купить весь образ"
        variant="right"
        width="33.75rem"
        backdrop="soft"
        className={styles.panel}
      >
        <div className={styles['panel-head']}>
          <h2 className={styles['panel-title']}>Купить весь образ</h2>
          <button
            type="button"
            className={styles.close}
            onClick={() => setOpen(false)}
            aria-label="Закрыть панель"
          >
            ×
          </button>
        </div>
        <div className={styles['panel-body']}>
          <ProductMedia
            media={null}
            fallbackLabel="Фото · весь образ"
            placeholderSub="модель в полном образе"
            sizes="540px"
            tint={null}
            className={styles.cover}
          />
          {selected.map((item) => {
            const price = priceOf(item);
            const offColors = item.product.colors
              .filter((color) => !isColorAvailable(item.product, color.id))
              .map((color) => color.name);
            return (
              <div key={item.product.slug} className={styles.row}>
                <ProductMedia
                  media={primaryMedia(item.product)}
                  fallbackLabel={item.product.shortLabel}
                  sizes="96px"
                  className={styles['row-thumb']}
                  tint={colorTint(item.product, item.selection.colorId) ?? item.product.tint}
                  placeholderSub=""
                />
                <div className={styles['row-body']}>
                  <div className={styles['row-head']}>
                    <span className={styles['row-name']}>{item.product.name}</span>
                    <span className={styles['row-price']}>{price ? formatMoney(price) : '—'}</span>
                  </div>
                  <VariantPicker
                    idPrefix={`look-${item.product.slug}`}
                    product={item.product}
                    colorId={item.selection.colorId}
                    size={item.selection.size}
                    onColorChange={(colorId) => pickColor(item, colorId)}
                    onSizeChange={(size) => pickSize(item, size)}
                    density="look"
                    sizeNotes
                    tip={item.current ? undefined : tips[item.product.slug]}
                    colorNote={
                      offColors.length > 0 ? (
                        <span className={styles.caption}>
                          Нет в наличии: {offColors.join(', ')}
                        </span>
                      ) : null
                    }
                  />
                  <TextButton className={styles.remove} onClick={() => toggle(item.product.slug)}>
                    Убрать из образа
                  </TextButton>
                </div>
              </div>
            );
          })}
          <div className={styles['panel-total']}>
            <span>{countLabel}</span>
            <span className={styles['panel-sum']}>{formatMoney(rub(total))}</span>
          </div>
          {needSize && <span className={styles.need}>Выберите размеры для всех вещей</span>}
          {mutation.isError && (
            <span className={styles.need} role="alert">
              {mutation.error?.message}
            </span>
          )}
          <Button
            size="lg"
            fullWidth
            className={styles.cta}
            loading={mutation.isPending}
            aria-disabled={!selected.length || needSize || undefined}
            onClick={addAll}
          >
            Добавить образ в корзину — {formatMoney(rub(total))}
          </Button>
          {addedCount !== null && (
            <output className={styles.added}>
              <span>
                Добавлено в корзину: {formatCount(addedCount, THINGS_FORMS)} отдельными позициями
              </span>
              <TextLink href="/cart">Перейти в корзину</TextLink>
            </output>
          )}
        </div>
      </Dialog>
    </>
  );
}
